import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AgentSidebar from "../components/AgentSidebar";
import CountrySelect from "../components/CountrySelect";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;
const badge = (status) =>
  ({
    PUBLISHED: "bg-emerald-100 text-emerald-700",
    PENDING_REVIEW: "bg-amber-100 text-amber-700",
    PROCESSING: "bg-blue-100 text-blue-700",
    REJECTED: "bg-slate-200 text-slate-600",
    FAILED: "bg-red-100 text-red-700",
  })[status] || "bg-slate-100 text-slate-600";

// Anything still "in flight" that we should keep polling for.
const isInFlight = (institutions, imports) =>
  imports.some((b) => b.status === "PROCESSING") ||
  institutions.some((i) => i.reviewStatus === "PROCESSING");

export default function InstitutionWorkspace({ isAdmin = false }) {
  const token = localStorage.getItem(isAdmin ? "adminToken" : "token");
  const [institutions, setInstitutions] = useState([]);
  const [imports, setImports] = useState([]);
  const [country, setCountry] = useState("");
  const [document, setDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "",
    country: "",
    normalFee: "",
    discountFee: "",
    feeCurrency: "USD",
  });
  const [logo, setLogo] = useState(null);
  const [heroImage, setHeroImage] = useState(null);
  const [modal, setModal] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const pollRef = useRef(null);

  const load = async () => {
    const [a, b] = await Promise.all([
      fetch(`${API_BASE}/admin/institutions`, { headers }),
      fetch(`${API_BASE}/admin/institutions/imports`, { headers }),
    ]);
    const aj = await a.json();
    const bj = await b.json();
    setInstitutions(aj.data || []);
    setImports(bj.data || []);
    return { institutions: aj.data || [], imports: bj.data || [] };
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const { institutions: inst, imports: imp } = await load();
      if (cancelled) return;
      // Keep polling every 4s as long as something is still processing in the background.
      if (isInFlight(inst, imp)) {
        pollRef.current = setTimeout(tick, 4000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(pollRef.current);
    };
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!country || !document) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("country", country);
    fd.append("document", document);
    await fetch(`${API_BASE}/admin/institutions/imports`, {
      method: "POST",
      headers,
      body: fd,
    });
    setUploading(false);
    setDocument(null);
    setCountry("");
    const { institutions: inst, imports: imp } = await load();
    clearTimeout(pollRef.current);
    if (isInFlight(inst, imp))
      pollRef.current = setTimeout(function poll() {
        load().then(({ institutions, imports }) => {
          if (isInFlight(institutions, imports))
            pollRef.current = setTimeout(poll, 4000);
        });
      }, 4000);
  };
  const create = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => fd.append(key, value));
    if (logo) fd.append("logo", logo);
    if (heroImage) fd.append("heroImage", heroImage);
    await fetch(`${API_BASE}/admin/institutions`, {
      method: "POST",
      headers,
      body: fd,
    });
    setModal(false);
    setForm({
      name: "",
      country: "",
      normalFee: "",
      discountFee: "",
      feeCurrency: "USD",
    });
    setLogo(null);
    setHeroImage(null);
    load();
  };
  const review = async (id, decision) => {
    await fetch(`${API_BASE}/admin/institutions/${id}/review`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ decision }),
    });
    load();
  };
  const retry = async (id) => {
    await fetch(`${API_BASE}/admin/institutions/${id}/retry`, {
      method: "PATCH",
      headers,
    });
    // Restart polling immediately (same pattern as upload()) so the UI
    // reflects PROCESSING -> PENDING_REVIEW/FAILED without needing a manual refresh.
    const { institutions: inst, imports: imp } = await load();
    clearTimeout(pollRef.current);
    if (isInFlight(inst, imp)) {
      pollRef.current = setTimeout(function poll() {
        load().then(({ institutions, imports }) => {
          if (isInFlight(institutions, imports)) pollRef.current = setTimeout(poll, 4000);
        });
      }, 4000);
    }
  };
  const shown = institutions.filter((i) =>
    `${i.name} ${i.country}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="bg-[#f7f9fb] min-h-screen font-['Plus_Jakarta_Sans']">
      {isAdmin ? <AdminSidebar /> : <AgentSidebar />}
      <main className="pt-16 md:pt-0 md:ml-64 p-5 md:p-8">
        <header className="mb-7 flex flex-col md:flex-row gap-4 md:items-end justify-between">
          <div>
            <p className="text-xs font-bold tracking-widest text-[#365da3] uppercase">
              University catalogue
            </p>
            <h1 className="text-3xl font-bold text-[#00255a] mt-1">
              Institutions & review
            </h1>
            <p className="text-[#64748b] mt-2">
              Import a country list, verify AI research, and publish only
              approved universities.
            </p>
          </div>
          <button
            onClick={() => setModal(true)}
            className="px-5 py-3 rounded-lg bg-[#00255a] text-white font-semibold"
          >
            + Add one university
          </button>
        </header>
        <section className="bg-white rounded-2xl border border-[#e2e8f0] p-5 mb-7">
          <h2 className="font-bold text-[#00255a]">
            1. Import a country university list
          </h2>
          <p className="text-sm text-[#64748b] mt-1">
            One PDF, CSV, or text file per country. Use columns: University
            Name, Country, Normal Fee, Discount Fee, Currency.
          </p>
          <form
            onSubmit={upload}
            className="mt-4 flex flex-col md:flex-row gap-3"
          >
            <CountrySelect
              required
              value={country}
              onChange={setCountry}
              className="border rounded-lg px-4 py-3 flex-1"
            />
            <input
              required
              type="file"
              accept=".pdf,.csv,.txt,image/*"
              onChange={(e) => setDocument(e.target.files[0])}
              className="border rounded-lg px-3 py-2 flex-1"
            />
            <button
              disabled={uploading}
              className="px-5 py-3 rounded-lg bg-[#365da3] text-white font-semibold disabled:opacity-60"
            >
              {uploading ? "Starting…" : "Upload & enrich"}
            </button>
          </form>
        </section>
        {imports.length > 0 && (
          <section className="mb-7">
            <h2 className="font-bold text-[#00255a] mb-3">Recent imports</h2>
            <div className="grid md:grid-cols-3 gap-3">
              {imports.slice(0, 3).map((item) => (
                <div key={item.id} className="bg-white border rounded-xl p-4">
                  <p className="font-semibold text-[#00255a]">{item.country}</p>
                  <p className="text-sm text-[#64748b] truncate">
                    {item.sourceFileName}
                  </p>
                  <p className="mt-2 text-xs font-bold">
                    {item.extractedCount ?? 0} found · {item.status}
                  </p>
                  {/*item.status === "FAILED" && item.errorLog && (
                    <p className="mt-2 text-xs text-red-600 break-words">
                      {item.errorLog}
                    </p>
                  )*/}
                </div>
              ))}
            </div>
          </section>
        )}
        <section className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden">
          <div className="p-5 border-b">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search universities or countries…"
              className="border rounded-lg px-4 py-2.5 w-full md:w-96"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="p-4">University</th>
                  <th className="p-4">Fees</th>
                  <th className="p-4">AI profile</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Courses</th>
                  <th className="p-4">Actions</th>
                  {isAdmin && <th className="p-4">Review</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((i) => (
                  <tr key={i.id} className="border-t">
                    <td className="p-4">
                      <div className="flex gap-3 items-center">
                        {i.logoUrl ? (
                          <img
                            className="w-10 h-10 rounded object-cover"
                            src={i.logoUrl}
                            alt=""
                          />
                        ) : (
                          <span className="material-symbols-outlined text-[#365da3]">
                            account_balance
                          </span>
                        )}
                        <div>
                          <p className="font-bold text-[#00255a]">{i.name}</p>
                          <p className="text-sm text-slate-500">{i.country}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm">
                      {i.normalFee ? (
                        <>
                          <span className="line-through text-slate-400">
                            {i.feeCurrency} {i.normalFee}
                          </span>
                          <br />
                          <b>
                            {i.feeCurrency} {i.discountFee || i.normalFee}
                          </b>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-4 text-sm">
                      {i.websiteUrl ? (
                        <a
                          className="text-[#365da3] underline"
                          href={i.websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Website found
                        </a>
                      ) : (
                        "Awaiting / unavailable"
                      )}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-bold ${badge(i.reviewStatus)}`}
                      >
                        {i.reviewStatus.replace("_", " ")}
                      </span>
                      {/*i.reviewStatus === "FAILED" && i.reviewNote && (
                        <p className="mt-1 text-xs text-red-600 max-w-xs break-words">
                          {i.reviewNote}
                        </p>
                      )*/}
                      {i.reviewStatus === "FAILED" && (
                        <button
                          onClick={() => retry(i.id)}
                          className="mt-2 text-xs font-bold text-white px-2.5 py-1 rounded-md bg-[#00255a] hover:bg-[#00255a]/90"
                        >
                          Retry
                        </button>
                      )}
                    </td>
                    <td className="p-4">
                      <Link
                        to={`${isAdmin ? "/admin" : "/agent"}/institutions/${i.id}/courses`}
                        className="text-xs font-semibold text-[#365da3] underline whitespace-nowrap"
                      >
                        Tag courses
                      </Link>
                    </td>
                    <td className="p-4">
                      <Link
                        to={`${isAdmin ? "/admin" : "/agent"}/institutions/${i.id}`}
                        className="text-xs font-semibold px-3 py-2 rounded-lg bg-[#00255a] text-white whitespace-nowrap inline-block"
                      >
                        View / Edit
                      </Link>
                    </td>
                    {isAdmin && (
                      <td className="p-4">
                        {i.reviewStatus === "PENDING_REVIEW" && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => review(i.id, "accept")}
                              className="text-xs bg-emerald-600 text-white px-3 py-2 rounded"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => review(i.id, "reject")}
                              className="text-xs bg-red-600 text-white px-3 py-2 rounded"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        {modal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex p-4 items-center justify-center">
            <form
              onSubmit={create}
              className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-3"
            >
              <h2 className="font-bold text-xl text-[#00255a]">
                Add university and assets
              </h2>
              <p className="text-sm text-slate-500">
                The university is published immediately; add its logo and
                profile hero image here.
              </p>
              {[
                ["name", "University name"],
                ["country", "Country"],
                ["normalFee", "Normal fee"],
                ["discountFee", "Discount fee"],
              ].map(([key, label]) => (
                <input
                  key={key}
                  required={key === "name" || key === "country"}
                  type={key.includes("Fee") ? "number" : "text"}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={label}
                  className="w-full border rounded-lg px-4 py-3"
                />
              ))}
              <select
                value={form.feeCurrency}
                onChange={(e) =>
                  setForm({ ...form, feeCurrency: e.target.value })
                }
                className="w-full border rounded-lg px-4 py-3"
              >
                  <option>USD</option> {/* US Dollar */}
                  <option>EUR</option> {/* Euro */}
                  <option>GBP</option> {/* British Pound */}
                  <option>TRY</option> {/* Turkish Lira */}
                  <option>CHF</option> {/* Swiss Franc */}
                  <option>SEK</option> {/* Swedish Krona */}
                  <option>NOK</option> {/* Norwegian Krone */}
                  <option>DKK</option> {/* Danish Krone */}
                  <option>PLN</option> {/* Polish Zloty */}
                  <option>CZK</option> {/* Czech Koruna */}
                  <option>HUF</option> {/* Hungarian Forint */}
                  <option>RON</option> {/* Romanian Leu */}
                  <option>BGN</option> {/* Bulgarian Lev */}
                  <option>ISK</option> {/* Icelandic Krona */}
              </select>
              <label className="block text-sm">
                University logo{" "}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogo(e.target.files[0])}
                  className="block mt-1"
                />
              </label>
              <label className="block text-sm">
                Profile hero image{" "}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setHeroImage(e.target.files[0])}
                  className="block mt-1"
                />
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  className="flex-1 border rounded-lg py-3"
                >
                  Cancel
                </button>
                <button className="flex-1 bg-[#00255a] text-white rounded-lg py-3">
                  Save university
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}